<template>
  <auth-layout>
    <template v-if="state === 'expired'">
      <h1 class="pop-auth__h1">This link has expired</h1>
      <p class="pop-auth__sub">Reset links work once and for 30 minutes. Request a new one from the log in page.</p>
      <router-link class="pop-auth__btn pop-auth__btn--primary reset-page__btn" to="/forgotpassword">Request a new reset link</router-link>
    </template>
    <template v-else>
      <h1 class="pop-auth__h1">Choose a new password</h1>
      <p class="pop-auth__sub">Make it at least 8 characters.</p>
      <div v-if="secured" class="pop-auth__box pop-auth__box--info" style="margin-bottom: 18px" data-test="secured-notice">
        All devices were logged out. Choose a new password to finish securing your account.
      </div>
      <form novalidate @submit.prevent="submit">
        <label class="pop-auth__label" for="reset-password">New password</label>
        <password-field v-model="password" input-id="reset-password" placeholder="Create a password" autocomplete="new-password" :error="passwordError" :meter="true" @enter="submit" />
        <label class="pop-auth__label" for="reset-confirm" style="margin-top: 20px">Confirm password</label>
        <password-field v-model="confirm" input-id="reset-confirm" placeholder="Repeat the password" autocomplete="new-password" :error="confirmError" @enter="submit" />
        <div v-if="error" class="pop-auth__box pop-auth__box--danger">{{ error }}</div>
        <button type="submit" class="pop-auth__btn pop-auth__btn--primary" :disabled="busy || state === 'checking'" data-test="reset-submit">
          {{ busy ? 'Saving' : 'Save new password' }}
        </button>
      </form>
    </template>
    <template #footer>
      <router-link class="pop-auth__link" to="/login">Back to log in</router-link>
    </template>
  </auth-layout>
</template>

<script>
import AuthLayout from '../AuthLayout.vue'
import PasswordField from '../PasswordField.vue'
import { finishLogin } from '../session'
import { getDeviceId } from '@/utils/device-id'
import { segmentPage, segmentTrack } from '@/utils'

export default {
  name: 'ResetPasswordPage',
  components: { AuthLayout, PasswordField },
  data() {
    return { password: '', confirm: '', touched: false, busy: false, error: '', state: 'checking' }
  },
  computed: {
    token() {
      return String(this.$route.query.token || '')
    },
    secured() {
      return this.$route.query.secured === '1'
    },
    passwordError() {
      return this.touched && this.password.length < 8 ? 'Password needs at least 8 characters.' : ''
    },
    confirmError() {
      return this.touched && this.confirm !== this.password ? 'Passwords do not match.' : ''
    },
  },
  async mounted() {
    // The old session is dead after "Secure your account" and pointless during a reset.
    this.$store.commit('clearStore')
    segmentPage('ResetPassword', { secured: this.secured })
    if (!this.token) {
      this.state = 'expired'
      return
    }
    try {
      const response = await this.$store.dispatch('validateResetToken', { token: this.token })
      this.state = response.data && response.data.valid ? 'form' : 'expired'
    } catch (e) {
      console.log(e)
      this.state = 'form' // let the submit decide
    }
  },
  methods: {
    async submit() {
      this.touched = true
      this.error = ''
      if (this.passwordError || this.confirmError) return
      this.busy = true
      try {
        const response = await this.$store.dispatch('resetPasswordWithToken', {
          token: this.token, newPassword: this.password, deviceId: getDeviceId(),
        })
        const data = response.data || {}
        if (data.action === 'PASSWORD_UPDATED') {
          segmentTrack('password_reset_completed', { secured: this.secured })
          this.$notify({ group: 'info', type: 'success', text: 'Password updated.' })
          finishLogin(this, { token: data.token, user: data.user }, { event: 'login', eventData: { via: 'password_reset' } })
        } else if (data.action === 'TOKEN_INVALID') {
          this.state = 'expired'
        } else {
          this.error = data.msg || 'Could not update the password. Please try again.'
        }
      } catch (e) {
        console.log(e)
        this.error = 'Could not update the password. Please try again.'
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped>
.reset-page__btn { display: inline-flex; width: auto; padding: 13px 22px; text-decoration: none; }
</style>
